<script setup lang="ts">
import { computed, ref } from 'vue';
import { ModalDialog } from '@hfc/ui-core';
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import SmallBusinessCarrierBox from '@/application/smallBusiness/infrastructure/components/SmallBusinessCarrierBox.vue';
import PricingAndBenefits from '@/application/smallBusiness/infrastructure/components/PricingAndBenefits.vue';
import { useCarrierInfo } from '@/application/shared/infrastructure/composition/useCarrierInfo';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';

interface StickyBoxCarrier {
  id: number;
  code_name: string;
  plans: Array<SmallBusinessPlan>;
}

interface emitInterface {
  (event: 'remove-plan', plan: SmallBusinessPlan): void,
}

const emits = defineEmits<emitInterface>();

const props = defineProps<{
  plans: Array<SmallBusinessPlan>;
  disableRemovePlans?: boolean;
}>();

const { smallBusinessState } = useSmallBusinessStore();

const { getCarrierFromPlan } = useCarrierInfo();

const detailsModal = ref<SmallBusinessPlan | false>(false);

const stickyBoxCarriers = computed(() => getCarriersFromPlans());

const getCarriersFromPlans = (): Array<StickyBoxCarrier> => {
  const carriers: Array<StickyBoxCarrier> = [];

  props.plans.forEach((plan) => {
    const carrier = getCarrierFromPlan(plan);
    const foundCarrier = carriers.find((c) => c.id === carrier.id);
    if (foundCarrier) {
      foundCarrier.plans.push(plan);

      return;
    }

    carriers.push({
      id: carrier.id,
      code_name: carrier.code_name,
      plans: [plan],
    });
  });

  return carriers;
};

const showBenefits = (plan: SmallBusinessPlan): void => {
  detailsModal.value = plan;
};

const removePlan = (plan: SmallBusinessPlan): void => {
  emits('remove-plan', plan);
};
</script>
<template>
  <SmallBusinessCarrierBox
    v-for="carrier in stickyBoxCarriers"
    :key="carrier.id"
    :carrier-code-name="carrier.code_name"
    :plans="carrier.plans"
    :show-price-breakdown-button="false"
    :show-icons-on-options="false"
    :disable-remove="disableRemovePlans"
    @show-benefits="showBenefits"
    @remove-plan="removePlan" />
  <ModalDialog v-if="detailsModal" @close="detailsModal = false" :show-confirm-button="false" size="xl" full-height overlap>
    <template #content>
        <PricingAndBenefits :plan="detailsModal" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :dependent-contribution="smallBusinessState.application.contribution.dependent_amount" :employee-contribution="smallBusinessState.application.contribution.employee_amount" :contribution-type="smallBusinessState.application.contribution.type" />
    </template>
  </ModalDialog>
</template>
